import { VoiceAgentVoice } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useIsTouchDevice } from "@jugl-web/utils/hooks/useMediaQuery";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { VoiceAgentVoiceCard } from "./components/VoiceAgentVoiceCard";
import { useVoiceCatalog } from "./hooks/useVoiceCatalog";
import {
  VoiceAgentVoiceDetails,
  VoiceFilterValue,
  VoiceGender,
  VoiceStyle,
} from "./types";

export interface VoiceAgentVoicePickerProps {
  name: string;
  gender: VoiceFilterValue<VoiceGender>;
  style: VoiceFilterValue<VoiceStyle>;
  isDisabled?: boolean;
}

export const VoiceAgentVoicePicker: React.FC<VoiceAgentVoicePickerProps> = ({
  name,
  gender,
  style,
  isDisabled,
}) => {
  const { t } = useTranslations();
  const { control } = useFormContext();
  const voices = useVoiceCatalog();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingVoice, setPlayingVoice] = useState<VoiceAgentVoice | null>(
    null
  );
  const isTouchDevice = useIsTouchDevice();

  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    []
  );

  const visibleVoices = useMemo(
    () =>
      voices.filter(
        (voice) =>
          (gender === "all" || voice.gender === gender) &&
          (style === "all" || voice.style === style)
      ),
    [gender, style, voices]
  );

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingVoice(null);
  };

  /**
   * Starts the given voice sample, stopping any sample that was already playing.
   */
  const startPlayback = (voice: VoiceAgentVoiceDetails) => {
    stopPlayback();

    const audio = new Audio(voice.sound);
    audioRef.current = audio;

    // Both callbacks can fire after another sample has already taken over -
    // `play()` rejects with an AbortError as soon as `stopPlayback` pauses it -
    // so they may only reset the state they still own
    const resetIfStillPlaying = () => {
      if (audioRef.current === audio) {
        setPlayingVoice(null);
      }
    };

    audio.onended = resetIfStillPlaying;
    setPlayingVoice(voice.id);
    audio.play().catch(resetIfStillPlaying);
  };

  const togglePlayback = (voice: VoiceAgentVoiceDetails) => {
    if (playingVoice === voice.id) {
      stopPlayback();
      return;
    }

    startPlayback(voice);
  };

  /**
   * Selects a voice and, on touch devices, plays its preview (there is no play button).
   */
  const handleSelect = (
    voice: VoiceAgentVoiceDetails,
    onChange: (voiceId: VoiceAgentVoice) => void
  ) => {
    onChange(voice.id);

    if (isTouchDevice) {
      startPlayback(voice);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) =>
        visibleVoices.length === 0 ? (
          <span className="font-secondary text-sm leading-[160%] text-[#828282]">
            {t(
              {
                id: "client-support-page.no-matching-voices-found",
                defaultMessage:
                  "No <highlightedText>matching voices</highlightedText> found",
              },
              {
                highlightedText: (text: ReactNode) => (
                  <span className="text-primary-800">{text}</span>
                ),
              }
            )}
          </span>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-4">
            {visibleVoices.map((voice) => (
              <VoiceAgentVoiceCard
                key={voice.id}
                voice={voice}
                isSelected={value === voice.id}
                isPlaying={playingVoice === voice.id}
                isDisabled={isDisabled}
                hidePlayButton={isTouchDevice}
                onSelect={() => handleSelect(voice, onChange)}
                onTogglePlay={() => togglePlayback(voice)}
              />
            ))}
          </div>
        )
      }
    />
  );
};
